import { useCallback, useEffect, useRef, useState } from 'react';

const INITIAL_COUNT = 10;
const BATCH_SIZE = 10;

function findScrollParent(node: HTMLElement): HTMLElement | null {
  let current = node.parentElement;
  while (current) {
    const { overflowY } = getComputedStyle(current);
    if (overflowY === 'auto' || overflowY === 'scroll') return current;
    current = current.parentElement;
  }
  return null;
}

export function useProgressiveRender<T>(items: T[], initialCount = INITIAL_COUNT, batchSize = BATCH_SIZE) {
  const [visibleCount, setVisibleCount] = useState(initialCount);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const pendingScrollId = useRef<string | null>(null);

  useEffect(() => {
    setVisibleCount(initialCount);
  }, [items, initialCount]);

  useEffect(() => {
    if (pendingScrollId.current) {
      const id = pendingScrollId.current;
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
        pendingScrollId.current = null;
      });
    }
  }, [visibleCount]);

  const scrollToId = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    pendingScrollId.current = id;
    setVisibleCount(items.length);
  }, [items.length]);

  const setSentinelRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    if (node) {
      const scrollParent = findScrollParent(node);
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            setVisibleCount((prev) => Math.min(prev + batchSize, items.length));
          }
        },
        { root: scrollParent, rootMargin: '200px' },
      );
      observerRef.current.observe(node);
    }

    sentinelRef.current = node;
  }, [items.length, batchSize]);

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  const visibleItems = items.slice(0, visibleCount);
  const hasMore = visibleCount < items.length;

  return { visibleItems, hasMore, sentinelRef: setSentinelRef, scrollToId };
}
